import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { filterPromotionsByTypesMarketing } from '@services/products';
import { getSessionVariable, setSessionVariable } from '@services/storage';

import { DiscountType } from '@app-types/discount';
import { UserType } from '@app-types/user';

import { getMarketings } from '@helpers/marketings';

import { MarketingType } from '@constants/plan';

export type MarketingsState = {
    [MarketingType.MOBILE]: boolean;
    [MarketingType.HOME]: boolean;
};
export type PartialMarketingsState = Partial<MarketingsState>;
const SESSION_KEY = 'promoConfigurator';

export type StoredMarketings = {
    home?: boolean;
    mobile?: boolean;
};

export function useMarketings(
    discounts: DiscountType[] = [],
    user?: UserType,
): [MarketingsState, (marketings: PartialMarketingsState) => MarketingsState] {
    const queryParams = useSearchParams();
    const [marketings, setMarketings] = useState<MarketingsState>({
        [MarketingType.MOBILE]: queryParams.has('clientmobile') ? queryParams.get('clientmobile') === 'true' : true,
        [MarketingType.HOME]: queryParams.has('clientbbox') ? queryParams.get('clientbbox') === 'true' : true,
    });

    const handleSetMarketings = (partialMarketings: PartialMarketingsState): MarketingsState => {
        const newMarketings: MarketingsState = {
            ...marketings,
            ...partialMarketings,
        };
        setMarketings(newMarketings);

        setStoredMarketings(newMarketings);

        return newMarketings;
    };

    const getStoredMarketings = () => {
        return JSON.parse(getSessionVariable(SESSION_KEY) ?? '{}') as StoredMarketings;
    };

    const setStoredMarketings = (marketings: PartialMarketingsState) => {
        const storedMarketings = getStoredMarketings();

        const data: StoredMarketings = {
            ...storedMarketings,
        };

        if (marketings[MarketingType.MOBILE] !== undefined) {
            data.mobile = marketings[MarketingType.MOBILE];
        }

        if (marketings[MarketingType.HOME] !== undefined) {
            data.home = marketings[MarketingType.HOME];
        }

        setSessionVariable(SESSION_KEY, JSON.stringify(data));
    };

    useEffect(() => {
        let newMarketingPlan: MarketingsState = {
            [MarketingType.MOBILE]: true,
            [MarketingType.HOME]: true,
        };

        newMarketingPlan = getMarketings(newMarketingPlan);

        const marketingsFromParams: PartialMarketingsState = {
            [MarketingType.MOBILE]: queryParams?.get('clientmobile')
                ? queryParams?.get('clientmobile') === 'true'
                : undefined,
            [MarketingType.HOME]: queryParams?.get('clientbbox')
                ? queryParams?.get('clientbbox') === 'true'
                : undefined,
        };

        if (marketingsFromParams[MarketingType.MOBILE] !== undefined) {
            newMarketingPlan[MarketingType.MOBILE] = marketingsFromParams[MarketingType.MOBILE];
        }

        if (marketingsFromParams[MarketingType.HOME] !== undefined) {
            newMarketingPlan[MarketingType.HOME] = marketingsFromParams[MarketingType.HOME];
        }

        if (user) {
            // Check for HOME promotion with 'estIncitation' = false
            newMarketingPlan = {
                ...newMarketingPlan,
                ...setReadOnlyMarketings(discounts),
            };
        }
        handleSetMarketings(newMarketingPlan);
    }, [queryParams]);

    return [marketings, handleSetMarketings];
}

export function setReadOnlyMarketings(discounts: DiscountType[]): PartialMarketingsState {
    return {
        [MarketingType.HOME]: filterPromotionsByTypesMarketing(discounts, [MarketingType.HOME], false, true).length > 0,
    };
}
